import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { BookOpen, Plus, ArrowUp, ArrowDown, X, Sparkle, MagnifyingGlass, Warning, DownloadSimple } from "@phosphor-icons/react";
import { openPdf } from "../lib/download";

const CONTENT_LABEL = { structured: "", attachment: "attachment", empty: "needs content", no_revision: "no published version" };

function ContentFlag({ s }) {
  if (s === "structured") return null;
  return <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><Warning size={11} weight="bold" />{CONTENT_LABEL[s]}</span>;
}

export default function PolicyBooks() {
  const [collections, setCollections] = useState([]);
  const [selId, setSelId] = useState(null);
  const [data, setData] = useState(null); // {collection, included, available}
  const [q, setQ] = useState("");
  const [pick, setPick] = useState({});   // available doc id -> selected
  const [suggest, setSuggest] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newKind, setNewKind] = useState("manual");
  const [newDesc, setNewDesc] = useState("");
  const [renaming, setRenaming] = useState(false);

  const loadCols = useCallback(() => api.get("/policies/collections").then((r) => setCollections(r.data)).catch(() => {}), []);
  useEffect(() => { loadCols(); }, [loadCols]);
  const loadManage = useCallback((cid) => api.get(`/policies/collections/${cid}/manage`).then((r) => { setData(r.data); setPick({}); }).catch((e) => toast.error(e?.response?.data?.detail || "Load failed")), []);
  useEffect(() => { if (selId) loadManage(selId); }, [selId, loadManage]);

  const create = async () => {
    if (!newName.trim()) return toast.error("Name required");
    const r = await api.post("/policies/collections", { name: newName, kind: newKind, description: newDesc || null });
    toast.success(newKind === "manual" ? "Book created" : "Pack created"); setNewName(""); setNewDesc(""); setNewKind("manual"); setCreateOpen(false); await loadCols(); setSelId(r.data.id);
  };
  const rename = async () => {
    await api.put(`/policies/collections/${selId}`, { name: data.collection.name });
    toast.success("Renamed"); setRenaming(false); loadCols();
  };
  const addPicked = async () => {
    const ids = Object.keys(pick).filter((k) => pick[k]);
    if (!ids.length) return toast.error("Select documents to add");
    await api.post(`/policies/collections/${selId}/items/add`, { rams_ids: ids });
    toast.success(`Added ${ids.length}`); loadManage(selId); loadCols();
  };
  const remove = async (rid) => {
    await api.post(`/policies/collections/${selId}/items/remove`, { rams_ids: [rid] });
    toast.success("Removed from book (policy kept)"); loadManage(selId); loadCols();
  };
  const reorder = async (idx, dir) => {
    const order = data.included.map((i) => i.rams_id);
    const j = idx + dir;
    if (j < 0 || j >= order.length) return;
    [order[idx], order[j]] = [order[j], order[idx]];
    await api.post(`/policies/collections/${selId}/items`, { rams_ids: order });
    loadManage(selId);
  };
  const openSuggest = async () => {
    const r = await api.get(`/policies/collections/${selId}/suggest`);
    setSuggest(r.data.reduce((a, s) => ({ ...a, [s.id]: { ...s, sel: true } }), {}));
  };
  const applySuggest = async () => {
    const ids = Object.values(suggest).filter((s) => s.sel).map((s) => s.id);
    if (!ids.length) { setSuggest(null); return; }
    await api.post(`/policies/collections/${selId}/items/add`, { rams_ids: ids });
    toast.success(`Added ${ids.length} suggested`); setSuggest(null); loadManage(selId); loadCols();
  };

  const filtered = (data?.available || []).filter((d) => !q || `${d.title} ${d.ref}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-5" data-testid="policy-books">
      <div>
        <Button data-testid="new-book-btn" size="sm" onClick={() => setCreateOpen(true)} className="w-full mb-3 bg-emerald-700 hover:bg-emerald-800 gap-1"><Plus size={15} /> New book / pack</Button>
        <div className="flex flex-col gap-1.5" data-testid="book-list">
          {collections.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No books yet.</p>}
          {collections.map((c) => (
            <button key={c.id} data-testid={`book-item-${c.id}`} onClick={() => setSelId(c.id)}
              className={`text-left rounded-lg border p-2.5 flex items-center gap-2 ${selId === c.id ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <BookOpen size={18} weight="bold" className="text-emerald-700 shrink-0" />
              <div className="min-w-0"><div className="font-semibold text-slate-800 text-sm truncate">{c.name}</div>
                <div className="text-[11px] text-slate-400">{c.item_count} documents{c.kind && c.kind !== "manual" ? ` · ${c.kind} pack` : ""}</div></div>
            </button>
          ))}
        </div>
      </div>

      <div>
        {!data ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500 text-sm">Select a book to manage its contents, or create one.</div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
              {renaming ? (
                <>
                  <Input data-testid="rename-input" value={data.collection.name} onChange={(e) => setData({ ...data, collection: { ...data.collection, name: e.target.value } })} className="flex-1" />
                  <Button size="sm" data-testid="rename-save" onClick={rename} className="bg-emerald-700 hover:bg-emerald-800">Save</Button>
                </>
              ) : (
                <>
                  <h3 className="font-display text-lg font-bold text-slate-900 flex-1">{data.collection.name}</h3>
                  <Button size="sm" variant="outline" data-testid="rename-btn" onClick={() => setRenaming(true)}>Rename</Button>
                  <Button size="sm" variant="outline" data-testid="suggest-btn" onClick={openSuggest} className="gap-1"><Sparkle size={14} /> Suggest H&amp;S</Button>
                  <Button size="sm" variant="outline" data-testid="export-manual-btn" onClick={() => openPdf(`/policies/collections/${selId}/export.pdf`)} className="gap-1"><DownloadSimple size={14} /> Export PDF</Button>
                </>
              )}
            </div>

            {/* contents / preview */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-slate-800 text-sm">Contents ({data.included.length})</h4>
                <span className="text-xs text-slate-400">Reorder = chapter order in the book</span>
              </div>
              <div className="flex flex-col gap-1.5" data-testid="book-contents">
                {data.included.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No documents in this book yet. Add from the list below or use “Suggest H&amp;S”.</p>}
                {data.included.map((it, i) => (
                  <div key={it.rams_id} data-testid={`content-${it.rams_id}`} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                    <span className="font-mono text-xs text-slate-400 w-6">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800 truncate">{it.title}</div>
                      <div className="text-[11px] text-slate-400 font-mono flex items-center gap-2">{it.ref}<ContentFlag s={it.content_state} /></div>
                    </div>
                    {!it.unavailable && <>
                      <button data-testid={`content-up-${it.rams_id}`} onClick={() => reorder(i, -1)} className="p-1 text-slate-400 hover:text-slate-700"><ArrowUp size={14} /></button>
                      <button data-testid={`content-down-${it.rams_id}`} onClick={() => reorder(i, 1)} className="p-1 text-slate-400 hover:text-slate-700"><ArrowDown size={14} /></button>
                    </>}
                    <button data-testid={`content-remove-${it.rams_id}`} onClick={() => remove(it.rams_id)} className="p-1 text-slate-400 hover:text-red-600"><X size={15} /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* available bulk add */}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-2 gap-2">
                <h4 className="font-semibold text-slate-800 text-sm">Add existing policies</h4>
                <div className="relative flex-1 max-w-xs">
                  <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="available-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-8 h-9" />
                </div>
                <Button size="sm" data-testid="add-picked-btn" onClick={addPicked} className="bg-slate-800 hover:bg-slate-900">Add selected</Button>
              </div>
              <div className="max-h-[320px] overflow-y-auto flex flex-col gap-1" data-testid="available-list">
                {filtered.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No more documents to add.</p>}
                {filtered.map((d) => (
                  <label key={d.id} data-testid={`available-${d.id}`} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" data-testid={`available-check-${d.id}`} checked={!!pick[d.id]} onChange={(e) => setPick({ ...pick, [d.id]: e.target.checked })} />
                    <span className="text-sm text-slate-800 flex-1 truncate">{d.title}</span>
                    <ContentFlag s={d.content_state} />
                    <span className="text-[11px] text-slate-400 font-mono">{d.ref}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">Adding to a book uses the same managed document — it does not copy it, assign it to staff, or create compliance failures.</p>
            </div>
          </div>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
        <DialogContent data-testid="create-book-dialog">
          <DialogHeader><DialogTitle>New book / pack</DialogTitle>
            <DialogDescription>A book is a curated ordered collection of existing policies. Use a client or job pack to assemble a document set for a specific customer or site — chapter order is set by reordering the contents.</DialogDescription></DialogHeader>
          <div className="flex flex-col gap-3">
            <Input data-testid="new-book-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Health & Safety Manual, or Acme Ltd — Site Pack" />
            <label className="text-xs font-semibold text-slate-600">Type
              <select data-testid="new-book-kind" value={newKind} onChange={(e) => setNewKind(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
                <option value="manual">Manual / book</option>
                <option value="client">Client pack</option>
                <option value="job">Job / site pack</option>
              </select></label>
            <Input data-testid="new-book-desc" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Client / job reference (optional)" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button data-testid="create-book-confirm" onClick={create} className="bg-emerald-700 hover:bg-emerald-800">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!suggest} onOpenChange={(o) => !o && setSuggest(null)}>
        <DialogContent data-testid="suggest-dialog">
          <DialogHeader><DialogTitle>Suggested Health &amp; Safety policies</DialogTitle>
            <DialogDescription>Review and untick anything that doesn’t belong, then apply in bulk. Nothing is added until you confirm.</DialogDescription></DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto flex flex-col gap-1">
            {suggest && Object.values(suggest).length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No obvious H&amp;S matches to suggest.</p>}
            {suggest && Object.values(suggest).map((s) => (
              <label key={s.id} data-testid={`suggest-${s.id}`} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={s.sel} onChange={(e) => setSuggest({ ...suggest, [s.id]: { ...s, sel: e.target.checked } })} />
                <span className="text-sm text-slate-800 flex-1 truncate">{s.title}</span>
                <span className="text-[10px] text-emerald-700">{(s.matched || []).join(", ")}</span>
                <span className="text-[11px] text-slate-400 font-mono">{s.ref}</span>
              </label>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSuggest(null)}>Cancel</Button>
            <Button data-testid="suggest-apply" onClick={applySuggest} className="bg-emerald-700 hover:bg-emerald-800">Add selected</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
